import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

import { exploreSplashArt } from './explore-splash-art';
import './ExploreSplash.css';

export interface ExploreSplashProps {
  active: boolean;
  phase: 'realm' | 'location' | 'world' | 'map' | 'home' | 'decode';
  holdUntilReady?: boolean;
  error?: string;
  onLeave?: () => void;
  onRetry?: () => void;
  onAlternative?: () => void;
}

const mobileQuery = '(max-width: 760px)';
const mobileSnapshot = () =>
  typeof window !== 'undefined' && window.matchMedia(mobileQuery).matches;
const serverSnapshot = () => false;
const subscribeMobile = (notify: () => void) => {
  const query = window.matchMedia(mobileQuery);
  query.addEventListener('change', notify);
  return () => query.removeEventListener('change', notify);
};

/** Presentation only: the caller owns location/map readiness and all permission policy. */
export function ExploreSplash({
  active,
  phase,
  holdUntilReady,
  error,
  onLeave,
  onRetry,
  onAlternative,
}: ExploreSplashProps) {
  const [dismissed, setDismissed] = useState(false);
  // Unmounting the gate releases even its media subscription when loading ends.
  if (!active || (dismissed && !holdUntilReady)) return null;
  return (
    <MobileSplash
      phase={phase}
      holdUntilReady={holdUntilReady}
      error={error}
      onLeave={onLeave}
      onRetry={onRetry}
      onAlternative={onAlternative}
      onDismiss={() => setDismissed(true)}
    />
  );
}

function MobileSplash({
  ...props
}: Omit<ExploreSplashProps, 'active'> & { onDismiss: () => void }) {
  const mobile = useSyncExternalStore(subscribeMobile, mobileSnapshot, serverSnapshot);
  // No image element or CSS image exists on desktop, so its art is never requested there.
  return mobile ? <SplashDialog {...props} /> : null;
}

function SplashDialog({
  phase,
  onDismiss,
  holdUntilReady,
  error,
  onLeave,
  onRetry,
  onAlternative,
}: Omit<ExploreSplashProps, 'active'> & { onDismiss: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const captionId = useId();
  const [slow, setSlow] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const recovery =
    error ??
    (timedOut
      ? 'Your world is taking longer to open. Try again, or return to the menu.'
      : undefined);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus = document.activeElement;
    dialog.showModal();
    return () => {
      dialog.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 8_000);
    const timeout = holdUntilReady ? window.setTimeout(() => setTimedOut(true), 30_000) : undefined;
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(timeout);
    };
  }, [holdUntilReady]);

  return (
    <dialog
      ref={dialogRef}
      className="explore-splash"
      aria-labelledby={titleId}
      aria-describedby={captionId}
      tabIndex={-1}
      onCancel={(event) => {
        event.preventDefault();
        if (holdUntilReady) onLeave?.();
        else onDismiss();
      }}
    >
      <img
        className="explore-splash__art"
        src={exploreSplashArt.url}
        width={exploreSplashArt.width}
        height={exploreSplashArt.height}
        alt=""
        decoding="async"
        fetchPriority="high"
      />
      <div className="explore-splash__content">
        <div className="explore-splash__actions">
          {holdUntilReady ? (
            onLeave && (
              <button type="button" className="explore-splash__dismiss" onClick={onLeave}>
                Back to menu
              </button>
            )
          ) : (
            <button type="button" className="explore-splash__dismiss" onClick={onDismiss}>
              Show map <span aria-hidden="true">↗</span>
            </button>
          )}
        </div>
        <header className="explore-splash__brand">
          <p className="explore-splash__title">Aztec Realms</p>
          <p className="explore-splash__edition">Runes &amp; Tribes</p>
          <h2 id={titleId} className="explore-splash__explore">
            {phase === 'home' ? 'Your settlement' : phase === 'decode' ? 'Decode' : 'Explore'}
          </h2>
        </header>
        <div className="explore-splash__status" role="status" aria-live="polite" aria-atomic="true">
          <span className="explore-splash__seal" aria-hidden="true">
            ◇
          </span>
          <p className="explore-splash__phase">
            {recovery
              ? 'Let’s get you back on your path'
              : {
                  realm: 'Opening your realm',
                  location: 'Finding your location',
                  world: 'Discovering nearby places',
                  map: 'Opening nearby paths',
                  home: 'Preparing your settlement',
                  decode: 'Preparing your glyph',
                }[phase]}
          </p>
          {slow && <p className="explore-splash__slow">Taking a little longer</p>}
          <p id={captionId} className="explore-splash__caption">
            {recovery ??
              (slow
                ? holdUntilReady
                  ? 'We’re still preparing your world. It will open when it is ready.'
                  : 'You can show the map while we keep looking.'
                : 'A world of discoveries is waiting nearby.')}
          </p>
          {holdUntilReady && recovery && onRetry && (
            <button className="explore-splash__dismiss" type="button" onClick={onRetry}>
              Try again
            </button>
          )}
          {holdUntilReady && recovery && onAlternative && (
            <button className="explore-splash__dismiss" type="button" onClick={onAlternative}>
              Play Decode instead
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}
